import * as z from "zod";
import { userFacingError } from "../authorization";
import { copyAllCommitFiles } from "../commitFiles";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { mutator } from "./mutator";
import { assertNotGitSynced } from "./syncGit";
import { generateId } from "./utils";
import type { Context } from "../authorization";
import type { Transaction } from "@rocicorp/zero";

export const pushBranchToLiveSchema = z.object({
  sourceBranchId: z.string(),
  newCommitId: z.string(),
  message: z.string(),
});

export const pushBranchToLive = mutator({
  schema: pushBranchToLiveSchema,
  resolve: {
    sourceBranch: { kind: "branch", from: "sourceBranchId" },
  },
  permissions: [
    {
      permission: "space:push_live",
      scopeId: ({ args }) => args.sourceBranch.workflow.spaceId,
    },
  ],
  fn: async (opts) => {
    await assertNotGitSynced(opts.tx, opts.args.sourceBranch.workflow.spaceId);
    await pushBranchToLiveInTransaction(opts);
  },
});

export async function pushBranchToLiveInTransaction({
  tx,
  ctx,
  args: { newCommitId, message, sourceBranch },
  deleteSourceBranch = true,
}: {
  tx: Transaction<Schema>;
  ctx: Context;
  args: {
    newCommitId: string;
    message: string;
    sourceBranch: {
      id: string;
      name: string;
      workflowId: string;
      headCommitId: string | null;
    };
  };
  deleteSourceBranch?: boolean;
}) {
  const creator = creatorColumns(ctx);
  const actor = actorColumns(ctx);
  const now = Date.now();

  if (!sourceBranch.headCommitId) {
    throw userFacingError("Branch has no head commit");
  }

  const [mainBranch] = await tx.run(
    zql.workflowBranches
      .where("workflowId", sourceBranch.workflowId)
      .where("name", "main")
  );

  const mainBranchId = mainBranch ? mainBranch.id : generateId();
  const newChatId = generateId();

  if (mainBranch) {
    await tx.mutate.workflowCommits.insert({
      id: newCommitId,
      branchId: mainBranchId,
      parentCommitId: mainBranch.headCommitId,
      chatId: newChatId,
      message,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowBranches.update({
      id: mainBranchId,
      headCommitId: newCommitId,
    });
  } else {
    await tx.mutate.workflowBranches.insert({
      id: mainBranchId,
      workflowId: sourceBranch.workflowId,
      name: "main",
      headCommitId: newCommitId,
      createdAt: now,
      lastChatEntryAt: null,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowCommits.insert({
      id: newCommitId,
      branchId: mainBranchId,
      parentCommitId: null,
      chatId: newChatId,
      message,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });
  }

  await copyAllCommitFiles(
    { tx, commitId: sourceBranch.headCommitId, tenantId: ctx.tenantId },
    newCommitId
  );

  const allEntries = (
    await tx.run(
      zql.workflowCommits
        .where("branchId", sourceBranch.id)
        .related("chats", (c) =>
          c.related("entries", (e) =>
            e
              .related("userMessage")
              .related("assistantMessage")
              .related("toolCall")
              .related("assistantReasoning")
              .related("systemMessage")
              .related("userAction")
          )
        )
    )
  )
    .flatMap((commit) => commit.chats)
    .flatMap((chat) => chat.entries)
    .sort((a, b) => a.createdAt - b.createdAt);

  // user_actions has no service-account column, so a service-account push is
  // marked with a SystemMessage instead of a UserAction.
  const markerEntryType = actor.userId ? "UserAction" : "SystemMessage";

  await tx.mutate.chats.insert({
    id: newChatId,
    commitId: newCommitId,
    createdAt: now,
    lastChatEntryType: markerEntryType,
    tenantId: ctx.tenantId,
  });

  let previousNewId: string | null = null;
  for (const entry of allEntries) {
    const newId = generateId();
    await tx.mutate.chatEntries.insert({
      id: newId,
      chatId: newChatId,
      entryType: entry.entryType,
      createdAt: entry.createdAt,
      previousEntryId: previousNewId,
      tenantId: ctx.tenantId,
    });
    previousNewId = newId;
    if (entry.userMessage) {
      await tx.mutate.userMessages.insert({
        entryId: newId,
        userId: entry.userMessage.userId,
        serviceAccountId: entry.userMessage.serviceAccountId,
        text: entry.userMessage.text,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.assistantMessage) {
      await tx.mutate.assistantMessages.insert({
        entryId: newId,
        text: entry.assistantMessage.text,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.toolCall) {
      await tx.mutate.toolCalls.insert({
        entryId: newId,
        toolName: entry.toolCall.toolName,
        anthropicId: entry.toolCall.anthropicId,
        input: entry.toolCall.input,
        output: entry.toolCall.output,
        isError: entry.toolCall.isError,
        completedAt: entry.toolCall.completedAt,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.assistantReasoning) {
      await tx.mutate.assistantReasonings.insert({
        entryId: newId,
        text: entry.assistantReasoning.text,
        signature: entry.assistantReasoning.signature,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.systemMessage) {
      await tx.mutate.systemMessages.insert({
        entryId: newId,
        text: entry.systemMessage.text,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.userAction) {
      await tx.mutate.userActions.insert({
        entryId: newId,
        userId: entry.userAction.userId,
        actionType: entry.userAction.actionType,
        metadata: entry.userAction.metadata,
        tenantId: ctx.tenantId,
      });
    }
  }

  const actionEntryId = generateId();
  await tx.mutate.chatEntries.insert({
    id: actionEntryId,
    chatId: newChatId,
    entryType: markerEntryType,
    createdAt: now,
    previousEntryId: previousNewId,
    tenantId: ctx.tenantId,
  });
  if (actor.userId) {
    await tx.mutate.userActions.insert({
      entryId: actionEntryId,
      userId: actor.userId,
      actionType: "pushDraftToLive",
      metadata: { draftName: sourceBranch.name },
      tenantId: ctx.tenantId,
    });
  } else {
    await tx.mutate.systemMessages.insert({
      entryId: actionEntryId,
      text: `Pushed “${sourceBranch.name}” to live`,
      tenantId: ctx.tenantId,
    });
  }

  await tx.mutate.workflowBranches.update({
    id: mainBranchId,
    lastChatEntryAt: now,
    lastChatEntryType: markerEntryType,
  });

  const sourceContributors = await tx.run(
    zql.branchContributors.where("branchId", sourceBranch.id)
  );
  const sourceContributorByUserId = new Map(
    sourceContributors.map((c) => [c.userId, c])
  );
  // Contributors are the human facepile. Carry every source contributor onto
  // main, plus the pushing user; a pure service account pusher has no human to
  // add.
  const contributorIds = new Set(sourceContributorByUserId.keys());
  if (actor.userId) contributorIds.add(actor.userId);
  for (const contributorId of contributorIds) {
    const sourceContributor = sourceContributorByUserId.get(contributorId);
    await tx.mutate.branchContributors.upsert({
      branchId: mainBranchId,
      workflowId: sourceBranch.workflowId,
      userId: contributorId,
      tenantId: ctx.tenantId,
      ...(sourceContributor
        ? { lastMessagedAt: sourceContributor.lastMessagedAt }
        : {}),
    });
  }

  const [wf] = await tx.run(zql.workflows.where("id", sourceBranch.workflowId));
  await tx.mutate.workflows.update({
    id: sourceBranch.workflowId,
    updatedAt: now,
    pausedAt: null,
    ...(wf?.autofixBranchId === sourceBranch.id
      ? { autofixBranchId: null }
      : {}),
    ...(wf?.autoImproveBranchId === sourceBranch.id
      ? { autoImproveBranchId: null }
      : {}),
  });

  // Space-branch members tracking this draft re-pin to the merged commit:
  // deleting the branch would otherwise null workflowBranchId (FK) and leave
  // the member at its pre-draft fork pin, silently reverting the workflow on
  // the remote branch at the next git push.
  const spaceBranchMemberships = await tx.run(
    zql.spaceBranchMembers.where("workflowId", sourceBranch.workflowId)
  );
  for (const member of spaceBranchMemberships) {
    if (member.workflowBranchId !== sourceBranch.id) continue;
    await tx.mutate.spaceBranchMembers.update({
      spaceBranchId: member.spaceBranchId,
      workflowId: member.workflowId,
      forkCommitId: newCommitId,
      workflowBranchId: null,
    });
  }

  if (deleteSourceBranch) {
    await tx.mutate.workflowBranches.delete({ id: sourceBranch.id });
  }
}
